/* My Grievances — the employee side of the complaint channel.

   Same shape as the other self-service request screens (Leave, Reimbursement,
   IT Help Desk): a server-side Reactable of the employee's own records, a
   raise modal, and a detail modal that carries the conversation and the
   actions only the complainant may take (respond, withdraw, confirm closure,
   reopen). */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DatePicker, Select, Upload as AntUpload } from "antd";
import dayjs from "dayjs";
import {
    CheckCircle2, Eye, MessageSquare, Paperclip, RotateCcw, Send,
    ShieldAlert, Star, UploadCloud, X,
} from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { truncateText } from "../../components/common/CommonFunctions";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import { modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import {
    GRIEVANCE_ATTACHMENTS, GRIEVANCE_CATEGORIES, GRIEVANCE_CLOSE, GRIEVANCE_REOPEN,
    GRIEVANCE_RESPOND, GRIEVANCE_WITHDRAW, GRIEVANCES,
} from "../../constants/api";
import {
    ALLOWED_GRIEVANCE_EXTENSIONS, CONFIDENTIALITY_OPTIONS, GRIEVANCE_PRIORITIES,
    GRIEVANCE_STATUSES, MAX_GRIEVANCE_FILE_MB, RESOLUTION_TYPE_LABELS,
} from "../../constants/grievance";
import {
    AttachmentList, ConfidentialityBadge, DetailRows, Field, fmtDate, fmtDateTime,
    GrievanceHeader, PriorityBadge, SectionLabel, SlaBadge, StatusBadge, Thread, Timeline,
    TruncatedBlock,
} from "./GrievancePieces";
import { HandlerDetailModal } from "./GrievanceInbox";

const STATUS_BANNER = {
    submitted: { t: "Awaiting acknowledgement", d: "Your grievance has been filed and is waiting to be picked up." },
    acknowledged: { t: "Acknowledged", d: "Someone has read your grievance and is looking into it." },
    under_investigation: { t: "Under investigation", d: "Your grievance is being investigated." },
    awaiting_employee_response: { t: "Your response is needed", d: "The handler has asked you for more information." },
    escalated: { t: "Escalated", d: "Your grievance has been escalated for further review." },
    resolved: { t: "Resolved — please confirm", d: "Review the outcome, then confirm closure or reopen it." },
    closed: { t: "Closed", d: "This grievance is closed." },
    reopened: { t: "Reopened", d: "Your grievance has been reopened and is back with the handler." },
    withdrawn: { t: "Withdrawn", d: "You withdrew this grievance." },
};

const MyGrievances = () => {
    const { fetch, create } = useApiMethods();
    const [searchParams, setSearchParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [categories, setCategories] = useState([]);

    const [raiseOpen, setRaiseOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [handlerMode, setHandlerMode] = useState(false);

    const refresh = () => setServerTrigger((t) => !t);

    const loadCategories = useCallback(async () => {
        try {
            const res = await fetch(GRIEVANCE_CATEGORIES, { drop_down: "True" });
            if (res?.status === "success") setCategories(res.data || []);
        } catch (e) {
            ToastMsg("error", e.message);
        }
        // `fetch` is a fresh ref every render — leaving it out of deps avoids a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    const openDetail = useCallback(async (id, preferMine = false) => {
        const numericId = Number(id);
        if (!Number.isFinite(numericId) || numericId <= 0) return;
        if (preferMine) {
            setHandlerMode(false);
            setDetailId(numericId);
            return;
        }
        try {
            const res = await fetch(GRIEVANCES, { id: numericId });
            const canHandle = res?.status === "success" && res.data?.can_handle && !res.data?.is_mine;
            setHandlerMode(!!canHandle);
            setDetailId(numericId);
        } catch (e) {
            ToastMsg("error", e.message);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const linkedId = searchParams.get("id");
        if (linkedId) openDetail(linkedId);
    }, [searchParams, openDetail]);

    const closeDetail = () => {
        setDetailId(null);
        setHandlerMode(false);
        if (searchParams.has("id")) {
            const next = new URLSearchParams(searchParams);
            next.delete("id");
            setSearchParams(next, { replace: true });
        }
    };

    const columns = [
        {
            accessor: "code", Header: "Grievance", filterable: true, sortable: true,
            Cell: ({ value }) => <span className="font-bold text-sidebar-primary">{value ?? "-"}</span>,
        },
        {
            accessor: "subject", Header: "Subject", filterable: true,
            Cell: ({ value }) => (
                <span className="font-medium">{truncateText(value || "-", 40, "280px", "balance")}</span>
            ),
        },
        { accessor: "category_name", Header: "Category", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "priority", Header: "Priority",
            Cell: ({ value }) => <PriorityBadge priority={value} />,
        },
        {
            accessor: "status", Header: "Status", filterable: true,
            filterType: "select",
            filterOptions: GRIEVANCE_STATUSES.map((s) => ({ label: s.label, value: s.value })),
            Cell: ({ row }) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={row.status} resolutionType={row.resolution_type} />
                    <ConfidentialityBadge
                        confidentiality={row.confidentiality}
                        isSensitive={row.is_sensitive}
                    />
                </div>
            ),
        },
        {
            accessor: "created_date", Header: "Raised", sortable: true,
            Cell: ({ value }) => <span className="text-xs text-muted-foreground">{fmtDate(value)}</span>,
        },
        {
            accessor: "Action", Header: "",
            Cell: ({ row }) => (
                <Button
                    variant="ghost" size="sm" className="text-sidebar-primary"
                    onClick={(e) => { e.stopPropagation(); openDetail(row.id, true); }}
                >
                    <Eye className="h-4 w-4" />
                </Button>
            ),
        },
    ];

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={MessageSquare}
                title="My Grievances"
                subtitle="Raise a complaint or concern and track how it is handled"
                totalRecords={totalRecords}
                buttonEnable
                buttonName="Raise Grievance"
                setOpenDialog={() => setRaiseOpen(true)}
            />

            <Reactable
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={GRIEVANCES}
                filter={{ scope: "mine" }}
                serverSide
                serverTrigger={serverTrigger}
                isCSVRequired
                isPDFRequired
                tableHeight="calc(100vh - 330px)"
                rowSize="sm"
                onRowClick={(row) => openDetail(row.id, true)}
            />

            {raiseOpen && (
                <RaiseModal
                    categories={categories}
                    create={create}
                    onClose={() => setRaiseOpen(false)}
                    onSaved={() => { setRaiseOpen(false); refresh(); }}
                />
            )}

            {detailId && handlerMode && (
                <HandlerDetailModal
                    grievanceId={detailId}
                    fetch={fetch}
                    create={create}
                    onClose={closeDetail}
                    onChanged={refresh}
                />
            )}
            {detailId && !handlerMode && (
                <DetailModal
                    grievanceId={detailId}
                    fetch={fetch}
                    create={create}
                    onClose={closeDetail}
                    onChanged={refresh}
                />
            )}
        </ContainerCard>
    );
};

/* -------------------------------- raise ---------------------------------- */

const RaiseModal = ({ categories, create, onClose, onSaved }) => {
    const [form, setForm] = useState({
        category: null,
        priority: "medium",
        subject: "",
        description: "",
        incident_date: null,
        location: "",
        persons_involved: "",
        confidentiality: "normal",
    });
    const [files, setFiles] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const selected = categories.find((c) => c.id === form.category);

    /* A category can forbid anonymous filing, so switching to one that does
       has to pull the toggle back rather than let the submit fail server-side. */
    const onCategoryChange = (id) => {
        const next = categories.find((c) => c.id === id);
        setForm((f) => ({
            ...f,
            category: id,
            priority: next?.default_priority || f.priority,
            confidentiality:
                f.confidentiality === "anonymous" && next && !next.allow_anonymous
                    ? "normal"
                    : f.confidentiality,
        }));
    };

    const onPick = (file) => {
        const ext = `.${(file.name || "").split(".").pop()?.toLowerCase()}`;
        if (!ALLOWED_GRIEVANCE_EXTENSIONS.includes(ext)) {
            ToastMsg("error", `${file.name}: unsupported file type.`);
            return false;
        }
        if (file.size > MAX_GRIEVANCE_FILE_MB * 1024 * 1024) {
            ToastMsg("error", `${file.name} exceeds ${MAX_GRIEVANCE_FILE_MB}MB.`);
            return false;
        }
        setFiles((prev) => [...prev, file]);
        return false; // stop antd auto-upload; we post these ourselves
    };
    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const submit = async () => {
        if (!form.category) { setErr("Please choose a category."); return; }
        if (form.subject.trim().length < 5) { setErr("Please enter a subject of at least 5 characters."); return; }
        if (form.description.trim().length < 20) {
            setErr("Please describe the grievance in at least 20 characters.");
            return;
        }
        setErr("");
        setLoading(true);
        try {
            const res = await create(GRIEVANCES, {
                category: form.category,
                priority: form.priority,
                subject: form.subject.trim(),
                description: form.description.trim(),
                incident_date: form.incident_date ? form.incident_date.format("YYYY-MM-DD") : null,
                location: form.location.trim(),
                persons_involved: form.persons_involved.trim()
                    ? { note: form.persons_involved.trim() }
                    : {},
                confidentiality: form.confidentiality,
            });

            if (res?.status !== "success") {
                ToastMsg("error", res?.message || "Could not raise grievance.");
                return;
            }

            /* Attachments ride on their own endpoint, so they are posted after
               the record exists. A failed file must not discard the grievance
               itself — the employee is told which file to retry instead. */
            const grievanceId = res.data?.id;
            const failed = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("grievance_id", String(grievanceId));
                fd.append("file", file);
                // eslint-disable-next-line no-await-in-loop
                const up = await create(GRIEVANCE_ATTACHMENTS, fd, true);
                if (up?.status !== "success") failed.push(file.name);
            }

            ToastMsg("success", res.message || "Grievance raised.");
            if (failed.length) {
                ToastMsg("warning", `Grievance saved, but these files did not upload: ${failed.join(", ")}`);
            }
            onSaved();
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogModal
            open
            onClose={onClose}
            onSave={submit}
            title="Raise a Grievance"
            saveLabel="Submit Grievance"
            loading={loading}
            width="72rem"
            maxWidth="96vw"
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Category" required>
                            <Select
                                size="large"
                                value={form.category}
                                onChange={onCategoryChange}
                                className="w-full"
                                placeholder="Select a category"
                                showSearch
                                optionFilterProp="label"
                                options={categories.map((c) => ({ label: c.name, value: c.id }))}
                                notFoundContent="No categories configured"
                                disabled={loading}
                            />
                        </Field>

                        <Field label="Priority" required>
                            <Select
                                size="large"
                                value={form.priority}
                                onChange={(v) => set("priority", v)}
                                className="w-full"
                                options={GRIEVANCE_PRIORITIES.map((p) => ({ label: p.label, value: p.value }))}
                                disabled={loading}
                            />
                        </Field>
                    </div>

                    {selected?.is_sensitive && (
                        <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/40 dark:bg-rose-900/20">
                            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-300" />
                            <p className="text-xs text-rose-800 dark:text-rose-200">
                                This is a restricted category. Only the designated handler, the
                                escalation contact, and organisation admins can see this grievance —
                                not your reporting manager, and not department admins or general HR.
                            </p>
                        </div>
                    )}

                    <Field label="Subject" required>
                        <Input
                            value={form.subject}
                            onChange={(e) => set("subject", e.target.value)}
                            placeholder="A one-line summary of your concern"
                            maxLength={200}
                            disabled={loading}
                            className={modalInputClass}
                        />
                    </Field>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Date of incident">
                            <DatePicker
                                size="large"
                                className="w-full"
                                value={form.incident_date}
                                onChange={(d) => set("incident_date", d)}
                                format="DD-MM-YYYY"
                                disabledDate={(d) => d && d > dayjs().endOf("day")}
                                disabled={loading}
                            />
                        </Field>
                        <Field label="Location">
                            <Input
                                value={form.location}
                                onChange={(e) => set("location", e.target.value)}
                                placeholder="Ward, department or site"
                                maxLength={150}
                                disabled={loading}
                            />
                        </Field>
                    </div>

                    <Field label="People involved" hint="Optional. Names or roles — you do not have to name anyone.">
                        <Input
                            value={form.persons_involved}
                            onChange={(e) => set("persons_involved", e.target.value)}
                            placeholder="e.g. shift supervisor, night duty team"
                            disabled={loading}
                        />
                    </Field>
                </div>

                <div className="space-y-4">
                    <Field
                        label="What happened"
                        required
                        hint="Include dates, what was said or done, and anything already tried."
                    >
                        <Textarea
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            rows={4}
                            placeholder="Describe the grievance"
                            disabled={loading}
                            className={modalTextareaClass}
                        />
                    </Field>

                    <Field label="Confidentiality" required>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {CONFIDENTIALITY_OPTIONS.map((opt) => {
                                const blocked = opt.value === "anonymous" && selected && !selected.allow_anonymous;
                                return (
                                    <label
                                        key={opt.value}
                                        className={cn(
                                            "flex cursor-pointer flex-col gap-1 rounded-lg border p-2.5 h-full",
                                            form.confidentiality === opt.value
                                                ? "border-sidebar-primary bg-sidebar-primary/5"
                                                : "border-gray-200 dark:border-gray-700",
                                            blocked && "cursor-not-allowed opacity-50",
                                        )}
                                    >
                                        <span className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                name="confidentiality"
                                                checked={form.confidentiality === opt.value}
                                                disabled={blocked || loading}
                                                onChange={() => set("confidentiality", opt.value)}
                                            />
                                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {opt.label}
                                            </span>
                                        </span>
                                        <span className="text-xs text-muted-foreground pl-6">{opt.hint}</span>
                                        {blocked && (
                                            <span className="text-xs text-muted-foreground pl-6">Not allowed for this category</span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Attachments" hint={`Up to ${MAX_GRIEVANCE_FILE_MB}MB each.`}>
                        <AntUpload beforeUpload={onPick} showUploadList={false} multiple disabled={loading}>
                            <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                disabled={loading}
                                className="text-foreground border-input bg-background hover:bg-accent hover:text-accent-foreground"
                            >
                                <UploadCloud className="h-4 w-4" /> Add file
                            </Button>
                        </AntUpload>
                        {files.length > 0 && (
                            <div className="mt-2 flex flex-col gap-1.5">
                                {files.map((f, i) => (
                                    <div
                                        key={`${f.name}-${i}`}
                                        className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                                    >
                                        <Paperclip className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{f.name}</span>
                                        <button
                                            type="button"
                                            className="ml-auto text-muted-foreground hover:text-destructive"
                                            onClick={() => removeFile(i)}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Field>
                </div>

                {err && (
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 lg:col-span-2">
                        {err}
                    </p>
                )}
            </div>
        </DialogModal>
    );
};

/* -------------------------------- detail --------------------------------- */

const DetailModal = ({ grievanceId, fetch, create, onClose, onChanged }) => {
    const [grievance, setGrievance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState("");
    const [replyFiles, setReplyFiles] = useState([]);
    const [rating, setRating] = useState(0);
    const [closeComment, setCloseComment] = useState("");
    const [reopenReason, setReopenReason] = useState("");
    const [withdrawReason, setWithdrawReason] = useState("");
    const [panel, setPanel] = useState(null); // 'close' | 'reopen' | 'withdraw'

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(GRIEVANCES, { id: grievanceId });
            if (res?.status === "success") setGrievance(res.data);
            else ToastMsg("error", res?.message || "Could not load grievance.");
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grievanceId]);

    useEffect(() => { load(); }, [load]);

    const act = async (url, payload, successFallback) => {
        setBusy(true);
        try {
            const res = await create(url, { id: grievanceId, ...payload });
            if (res?.status === "success") {
                ToastMsg("success", res.message || successFallback);
                setGrievance(res.data);
                setPanel(null);
                setReply("");
                setReplyFiles([]);
                setReopenReason("");
                setWithdrawReason("");
                onChanged();
                return true;
            }
            ToastMsg("error", res?.message || "Action failed.");
            return false;
        } catch (e) {
            ToastMsg("error", e.message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    if (loading || !grievance) {
        return (
            <DialogModal open onClose={onClose} title="Grievance" showActions={false} width="640px">
                <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
            </DialogModal>
        );
    }

    const banner = STATUS_BANNER[grievance.status] || STATUS_BANNER.submitted;
    const canRespond = grievance.status === "awaiting_employee_response";
    const canWithdraw = !["resolved", "closed", "withdrawn"].includes(grievance.status);
    const canCloseOrReopen = grievance.status === "resolved";

    return (
        <DialogModal
            open
            onClose={onClose}
            title={`Grievance ${grievance.code}`}
            showActions={false}
            width="680px"
        >
            <div className="space-y-4">
                <GrievanceHeader grievance={grievance} />

                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">{banner.t}</p>
                        <p className="text-xs text-muted-foreground">{banner.d}</p>
                    </div>
                    <SlaBadge sla={grievance.sla} />
                </div>

                <div>
                    <SectionLabel>What happened</SectionLabel>
                    <TruncatedBlock text={grievance.description} />
                </div>

                <div>
                    <SectionLabel>Details</SectionLabel>
                    <DetailRows
                        rows={[
                            ["Category", grievance.category?.name],
                            ["Incident date", grievance.incident_date ? fmtDate(grievance.incident_date) : "—"],
                            ["Location", grievance.location],
                            ["People involved", grievance.persons_involved?.note],
                            ["Acknowledged", fmtDateTime(grievance.acknowledged_at)],
                            ["Resolution due", fmtDateTime(grievance.sla?.resolution_due_at)],
                            ["Reopened", grievance.reopen_count ? `${grievance.reopen_count} time(s)` : "—"],
                        ]}
                    />
                </div>

                {grievance.resolution && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 dark:border-emerald-900/40 dark:bg-emerald-900/20">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                            Outcome · {RESOLUTION_TYPE_LABELS[grievance.resolution_type] || grievance.resolution_type}
                        </p>
                        <TruncatedBlock
                            text={grievance.resolution}
                            className="text-emerald-900 dark:text-emerald-100"
                        />
                        <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                            Resolved {fmtDateTime(grievance.resolved_at)}
                            {grievance.reopen_due_at && ` · you can reopen until ${fmtDate(grievance.reopen_due_at)}`}
                        </p>
                    </div>
                )}

                {Array.isArray(grievance.attachments) && grievance.attachments.length > 0 && (
                    <div>
                        <SectionLabel>Attachments</SectionLabel>
                        <AttachmentList attachments={grievance.attachments} />
                    </div>
                )}

                <div>
                    <SectionLabel>Conversation</SectionLabel>
                    <Thread comments={grievance.comments} />
                    {canRespond && (
                        <div className="mt-2.5 space-y-2">
                            <Textarea
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                rows={3}
                                placeholder="Answer the handler's question"
                                disabled={busy}
                            />
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <AntUpload
                                    beforeUpload={(file) => {
                                        const ext = `.${(file.name || "").split(".").pop()?.toLowerCase()}`;
                                        if (!ALLOWED_GRIEVANCE_EXTENSIONS.includes(ext)) {
                                            ToastMsg("error", `${file.name}: unsupported file type.`);
                                            return false;
                                        }
                                        if (file.size > MAX_GRIEVANCE_FILE_MB * 1024 * 1024) {
                                            ToastMsg("error", `${file.name} exceeds ${MAX_GRIEVANCE_FILE_MB}MB.`);
                                            return false;
                                        }
                                        setReplyFiles((prev) => [...prev, file]);
                                        return false;
                                    }}
                                    showUploadList={false}
                                    multiple
                                    disabled={busy}
                                >
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        type="button"
                                        disabled={busy}
                                        className="text-foreground border-input bg-background hover:bg-accent hover:text-accent-foreground"
                                    >
                                        <UploadCloud className="h-4 w-4" /> Add file
                                    </Button>
                                </AntUpload>
                                <Button
                                    size="sm"
                                    disabled={busy || !reply.trim()}
                                    onClick={async () => {
                                        const ok = await act(
                                            GRIEVANCE_RESPOND,
                                            { comment: reply.trim() },
                                            "Response sent.",
                                        );
                                        if (!ok || !replyFiles.length) return;
                                        const failed = [];
                                        for (const file of replyFiles) {
                                            const fd = new FormData();
                                            fd.append("grievance_id", String(grievanceId));
                                            fd.append("file", file);
                                            // eslint-disable-next-line no-await-in-loop
                                            const up = await create(GRIEVANCE_ATTACHMENTS, fd, true);
                                            if (up?.status !== "success") failed.push(file.name);
                                        }
                                        if (failed.length) {
                                            ToastMsg("warning", `Response saved, but these files did not upload: ${failed.join(", ")}`);
                                        }
                                        setReplyFiles([]);
                                        await load();
                                    }}
                                >
                                    <Send className="h-4 w-4" /> Send response
                                </Button>
                            </div>
                            {replyFiles.length > 0 && (
                                <div className="flex flex-col gap-1.5">
                                    {replyFiles.map((f, i) => (
                                        <div
                                            key={`${f.name}-${i}`}
                                            className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                                        >
                                            <Paperclip className="h-4 w-4 shrink-0" />
                                            <span className="truncate">{f.name}</span>
                                            <button
                                                type="button"
                                                className="ml-auto text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                                                onClick={() => setReplyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div>
                    <SectionLabel>Timeline</SectionLabel>
                    <Timeline entries={grievance.audit_trail} />
                </div>

                {panel === "close" && (
                    <div className="space-y-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900/40 dark:bg-emerald-900/10">
                        <Field label="How satisfied are you with the outcome?">
                            <div className="flex gap-1.5">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setRating(n)}
                                        className={cn(
                                            "rounded p-1",
                                            n <= rating ? "text-amber-500" : "text-gray-300 dark:text-gray-600",
                                        )}
                                    >
                                        <Star className="h-5 w-5" fill={n <= rating ? "currentColor" : "none"} />
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Anything to add?">
                            <Textarea
                                value={closeComment}
                                onChange={(e) => setCloseComment(e.target.value)}
                                rows={2}
                                disabled={busy}
                            />
                        </Field>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPanel(null)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => act(
                                    GRIEVANCE_CLOSE,
                                    { rating: rating || null, comment: closeComment.trim() },
                                    "Grievance closed.",
                                )}
                            >
                                <CheckCircle2 className="h-4 w-4" /> Confirm closure
                            </Button>
                        </div>
                    </div>
                )}

                {panel === "reopen" && (
                    <div className="space-y-2.5 rounded-lg border border-rose-200 bg-rose-50/60 p-3 dark:border-rose-900/40 dark:bg-rose-900/10">
                        <Field label="Why are you reopening this?" required>
                            <Textarea
                                value={reopenReason}
                                onChange={(e) => setReopenReason(e.target.value)}
                                rows={3}
                                placeholder="Explain what is still unresolved"
                                disabled={busy}
                            />
                        </Field>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPanel(null)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive" size="sm"
                                disabled={busy || !reopenReason.trim()}
                                onClick={() => act(GRIEVANCE_REOPEN, { comment: reopenReason.trim() }, "Grievance reopened.")}
                            >
                                <RotateCcw className="h-4 w-4" /> Reopen
                            </Button>
                        </div>
                    </div>
                )}

                {panel === "withdraw" && (
                    <div className="space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
                        <Field label="Reason for withdrawing" hint="Optional, but it helps HR understand what changed.">
                            <Textarea
                                value={withdrawReason}
                                onChange={(e) => setWithdrawReason(e.target.value)}
                                rows={2}
                                disabled={busy}
                            />
                        </Field>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPanel(null)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive" size="sm"
                                disabled={busy}
                                onClick={() => act(GRIEVANCE_WITHDRAW, { reason: withdrawReason.trim() }, "Grievance withdrawn.")}
                            >
                                <X className="h-4 w-4" /> Withdraw
                            </Button>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap justify-end gap-2.5 border-t border-gray-100 pt-4 dark:border-gray-800">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
                    {canWithdraw && !panel && (
                        <Button variant="outline" onClick={() => setPanel("withdraw")} disabled={busy}>
                            <X className="h-4 w-4" /> Withdraw
                        </Button>
                    )}
                    {canCloseOrReopen && !panel && (
                        <>
                            <Button variant="outline" onClick={() => setPanel("reopen")} disabled={busy}>
                                <RotateCcw className="h-4 w-4" /> Reopen
                            </Button>
                            <Button onClick={() => setPanel("close")} disabled={busy}>
                                <CheckCircle2 className="h-4 w-4" /> Confirm closure
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </DialogModal>
    );
};

export default MyGrievances;
